export type SpecPatch =
  | { op: "add"; path: string; value: unknown }
  | { op: "remove"; path: string }
  | { op: "replace"; path: string; value: unknown }

/** Escape a single key per RFC 6901: ~ -> ~0, / -> ~1 */
function escapePointer(key: string): string {
  return key.replace(/~/g, "~0").replace(/\//g, "~1")
}

/** Unescape a single JSON Pointer token: ~1 -> /, ~0 -> ~ */
function unescapePointer(token: string): string {
  return token.replace(/~1/g, "/").replace(/~0/g, "~")
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v)
}

/**
 * Compares two specs and returns a list of patches describing the differences.
 * Uses JSON Pointer paths (RFC 6901).
 */
export function diffToPatches(oldSpec: unknown, newSpec: unknown, basePath = ""): SpecPatch[] {
  // Reference equal — nothing changed
  if (oldSpec === newSpec) return []

  // If either is null/undefined, or they have different types, full replace
  if (
    oldSpec == null ||
    newSpec == null ||
    typeof oldSpec !== typeof newSpec ||
    Array.isArray(oldSpec) !== Array.isArray(newSpec)
  ) {
    return [{ op: "replace", path: basePath || "/", value: newSpec }]
  }

  // Arrays — treat atomically
  if (Array.isArray(oldSpec) && Array.isArray(newSpec)) {
    if (oldSpec.length !== newSpec.length) {
      return [{ op: "replace", path: basePath || "/", value: newSpec }]
    }
    for (let i = 0; i < oldSpec.length; i++) {
      if (oldSpec[i] !== newSpec[i]) {
        return [{ op: "replace", path: basePath || "/", value: newSpec }]
      }
    }
    return []
  }

  // Both plain objects — recurse
  if (isPlainObject(oldSpec) && isPlainObject(newSpec)) {
    const patches: SpecPatch[] = []
    const oldKeys = new Set(Object.keys(oldSpec))
    const newKeys = new Set(Object.keys(newSpec))

    // Added keys
    for (const key of newKeys) {
      const childPath = basePath + "/" + escapePointer(key)
      if (!oldKeys.has(key)) {
        patches.push({ op: "add", path: childPath, value: newSpec[key] })
      }
    }

    // Removed keys
    for (const key of oldKeys) {
      if (!newKeys.has(key)) {
        patches.push({
          op: "remove",
          path: basePath + "/" + escapePointer(key),
        })
      }
    }

    // Shared keys — recurse
    for (const key of oldKeys) {
      if (newKeys.has(key)) {
        const childPath = basePath + "/" + escapePointer(key)
        patches.push(...diffToPatches(oldSpec[key], newSpec[key], childPath))
      }
    }

    return patches
  }

  // Scalars with different values
  if (oldSpec !== newSpec) {
    return [{ op: "replace", path: basePath || "/", value: newSpec }]
  }

  return []
}

/**
 * Parse a JSON Pointer path into unescaped tokens.
 * "" -> [], "/foo/bar" -> ["foo","bar"]
 */
function parsePointer(path: string): string[] {
  if (path === "" || path === "/") return []
  // Remove leading "/"
  const raw = path.startsWith("/") ? path.slice(1) : path
  return raw.split("/").map(unescapePointer)
}

/**
 * Deep clone a value (simple JSON-safe clone).
 */
function deepClone<T>(value: T): T {
  if (value === null || typeof value !== "object") return value
  return JSON.parse(JSON.stringify(value)) as T
}

/**
 * Applies patches to a spec, preserving reference identity for unchanged subtrees
 * (structural sharing).
 */
export function applySpecPatches<T>(spec: T, patches: SpecPatch[]): T {
  if (patches.length === 0) return spec

  let result = deepClone(spec) as Record<string, unknown>

  for (const patch of patches) {
    const tokens = parsePointer(patch.path)

    if (tokens.length === 0) {
      // Patch targets the root
      if (patch.op === "replace" || patch.op === "add") {
        result = deepClone(patch.value) as Record<string, unknown>
      }
      continue
    }

    // Navigate to parent
    let current: Record<string, unknown> = result
    for (let i = 0; i < tokens.length - 1; i++) {
      const token = tokens[i]
      if (Array.isArray(current)) {
        current = (current as unknown[])[Number(token)] as Record<string, unknown>
      } else {
        current = current[token] as Record<string, unknown>
      }
    }

    const lastToken = tokens[tokens.length - 1]

    switch (patch.op) {
      case "add":
      case "replace":
        if (Array.isArray(current)) {
          ;(current as unknown[])[Number(lastToken)] = deepClone(patch.value)
        } else {
          current[lastToken] = deepClone(patch.value)
        }
        break
      case "remove":
        if (Array.isArray(current)) {
          ;(current as unknown[]).splice(Number(lastToken), 1)
        } else {
          delete current[lastToken]
        }
        break
    }
  }

  return result as unknown as T
}

/**
 * Returns a new spec with structural sharing -- unchanged subtrees keep their
 * reference identity so React.memo / useMemo can skip re-renders.
 */
export function structuralShare<T>(oldSpec: T, newSpec: T): T {
  if (oldSpec === newSpec) return oldSpec

  if (
    oldSpec == null ||
    newSpec == null ||
    typeof oldSpec !== typeof newSpec ||
    typeof oldSpec !== "object"
  ) {
    return newSpec
  }

  // Arrays
  if (Array.isArray(oldSpec) && Array.isArray(newSpec)) {
    if (oldSpec.length !== newSpec.length) {
      // Still share individual elements where possible
      const shared = newSpec.map((el, i) =>
        i < oldSpec.length ? structuralShare(oldSpec[i], el) : el
      )
      return shared as unknown as T
    }
    const shared = newSpec.map((el, i) => structuralShare(oldSpec[i], el))
    const allSame = shared.every((s, i) => s === oldSpec[i])
    return allSame ? oldSpec : (shared as unknown as T)
  }

  // Objects
  if (isPlainObject(oldSpec) && isPlainObject(newSpec)) {
    const oldObj = oldSpec as Record<string, unknown>
    const newObj = newSpec as Record<string, unknown>
    const oldKeys = Object.keys(oldObj)
    const newKeys = Object.keys(newObj)

    // Quick check: if key sets differ in size, objects differ
    const result: Record<string, unknown> = {}
    let allSame = oldKeys.length === newKeys.length

    for (const key of newKeys) {
      if (!Object.prototype.hasOwnProperty.call(oldObj, key)) {
        result[key] = newObj[key]
        allSame = false
      } else {
        const shared = structuralShare(oldObj[key], newObj[key])
        result[key] = shared
        if (shared !== oldObj[key]) allSame = false
      }
    }

    // Check for removed keys
    for (const key of oldKeys) {
      if (!Object.prototype.hasOwnProperty.call(newObj, key)) {
        allSame = false
      }
    }

    return allSame ? oldSpec : (result as unknown as T)
  }

  return newSpec
}
